import React, { useEffect, useState, useMemo } from "react";
import { CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import ApiCustom from "../../utils/ApiCustom";
import CustomTableComponent from "../../components/CustomTableComponent";
import dayjs from "dayjs";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { formatCurrencyVND } from "../../utils/helps";

// Animation cho modal/card
const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

// Modal chi tiết sản phẩm đã bán
const ProductDetailModal = ({ product, onClose }) => {
  const {
    urlImage,
    name,
    productCode,
    ordersIncluded = [],
    isContactPrice,
  } = product;

  // Range ngày
  const allDates = ordersIncluded.map((o) =>
    dayjs(o.createdAt).format("YYYY-MM-DD"),
  );
  const minDate = allDates.length
    ? allDates.reduce((a, b) => (a < b ? a : b))
    : "";
  const maxDate = dayjs().format("YYYY-MM-DD");
  const [fromDate, setFromDate] = useState(minDate);
  const [toDate, setToDate] = useState(maxDate);

  useEffect(() => {
    setFromDate(minDate);
    setToDate(maxDate);
    // eslint-disable-next-line
  }, [product?.productCode]);

  // Lọc đơn hàng theo khoảng thời gian
  const filteredOrders = useMemo(() => {
    return ordersIncluded.filter((o) => {
      const d = dayjs(o.createdAt).format("YYYY-MM-DD");
      return (!fromDate || d >= fromDate) && (!toDate || d <= toDate);
    });
  }, [ordersIncluded, fromDate, toDate]);

  const totalSold = filteredOrders.reduce(
    (sum, o) => sum + Number(o.quantity || 0),
    0,
  );

  // Biểu đồ
  const chartData = useMemo(() => {
    const map = {};
    filteredOrders.forEach((o) => {
      const date = dayjs(o.createdAt).format("YYYY-MM-DD");
      map[date] = (map[date] || 0) + Number(o.quantity || 0);
    });
    return Object.entries(map)
      .map(([date, value]) => ({ date, value }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredOrders]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
      <div
        className="relative bg-white w-full max-w-3xl mx-2 rounded-2xl p-7 shadow-xl overflow-y-auto"
        style={{ maxHeight: "92vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
        {/* Grid ảnh - info */}
        <div className="flex flex-col sm:flex-row gap-7 mb-6">
          <div className="flex-shrink-0 flex justify-center items-center">
            <div className="w-44 h-44 rounded-xl overflow-hidden border bg-gray-50 flex items-center justify-center shadow-sm">
              {urlImage ? (
                <img
                  src={urlImage}
                  alt={name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-gray-300">No image</span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 justify-center flex-1">
            <div className="text-2xl font-bold text-green-700">{name}</div>
            <div className="text-gray-600">
              <span className="font-medium text-gray-700">Mã sản phẩm: </span>
              <span className="font-mono">{productCode || "---"}</span>
            </div>
            <div className="text-gray-600">
              <span className="font-medium text-gray-700">Giá: </span>
              <span>
                {isContactPrice === true
                  ? "Liên hệ"
                  : formatCurrencyVND(product.price)}
              </span>
            </div>
            {/* Filter ngày + tổng đã bán */}
            <div className="flex flex-col md:flex-row md:items-center md:gap-3 mt-2">
              <div className="flex items-center gap-2 mb-2 md:mb-0">
                <span className="font-medium text-gray-500 text-sm">Từ</span>
                <input
                  type="date"
                  value={fromDate}
                  min={minDate}
                  max={toDate || maxDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="border rounded-lg px-2 py-1 text-sm text-gray-600 bg-gray-50"
                />
                <span className="font-medium text-gray-500 text-sm">đến</span>
                <input
                  type="date"
                  value={toDate}
                  min={fromDate || minDate}
                  max={maxDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="border rounded-lg px-2 py-1 text-sm text-gray-600 bg-gray-50"
                />
              </div>
              <div className="md:ml-4">
                <span className="font-medium text-gray-500 text-sm">
                  Đã bán:
                </span>
                <span className="text-lg font-bold text-green-800 ml-1">
                  {totalSold}
                </span>
                <span className="text-sm text-gray-600 ml-1">sản phẩm</span>
              </div>
            </div>
          </div>
        </div>
        {/* Biểu đồ */}
        <div className="mb-6">
          <div className="font-semibold text-green-700 mb-2">
            Số lượng bán theo ngày
          </div>
          <div className="bg-gray-50 rounded-xl border px-2 py-3">
            {chartData.length === 0 ? (
              <div className="text-gray-400 text-center py-8">
                Không có dữ liệu
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={chartData}>
                  <XAxis dataKey="date" />
                  <YAxis width={40} />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="value"
                    name="Số lượng bán"
                    stroke="#34d399"
                    fill="#bbf7d0"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        {/* Danh sách đơn */}
        <div>
          <div className="font-bold text-green-700 mb-1">
            {filteredOrders.length > 0
              ? `Có trong ${filteredOrders.length} đơn hàng:`
              : "Chưa có đơn hàng nào mua sản phẩm này trong khoảng này."}
          </div>
          {filteredOrders.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded shadow text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-700">
                    <th className="px-2 py-2 border">Mã đơn hàng</th>
                    <th className="px-2 py-2 border">Khách hàng</th>
                    <th className="px-2 py-2 border">Ngày giao dịch</th>
                    <th className="px-2 py-2 border">Số lượng sản phẩm</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((row, idx) => (
                    <tr key={row.orderId + "-" + idx}>
                      <td className="px-2 py-1 border text-center">
                        {row.orderCode}
                      </td>
                      <td className="px-2 py-1 border">{row.customer}</td>
                      <td className="px-2 py-1 border">
                        {row.createdAt
                          ? dayjs(row.createdAt).format("DD/MM/YYYY HH:mm")
                          : ""}
                      </td>
                      <td className="px-2 py-1 border text-center font-bold text-blue-700">
                        {row.quantity}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ProductSoldCard = () => {
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // 1. Lấy toàn bộ sản phẩm
  useEffect(() => {
    ApiCustom.get("/product-show")
      .then((res) => setAllProducts(res.data?.data?.product || []))
      .catch(() => setAllProducts([]));
  }, []);

  // 2. Lấy sản phẩm đã bán thành công từ các đơn duyệt
  useEffect(() => {
    setLoading(true);
    ApiCustom.get("/order-show")
      .then(async (res) => {
        const approvedOrders = (res.data?.data?.order || []).filter(
          (order) => order.status === "success",
        );
        if (!approvedOrders.length) {
          setProducts([]);
          setLoading(false);
          return;
        }
        // Lấy detail từng order và chuẩn bị mapping orderId, orderCode, customer, createdAt...
        const allDetailsArrays = await Promise.all(
          approvedOrders.map((order) =>
            ApiCustom.get(`/order-show-id/${order.id}`)
              .then((r) =>
                (r.data?.data?.details || []).map((d) => ({
                  ...d,
                  orderId: order.id,
                  orderCode: order.code || order.id,
                  customer: order.fullName || "---",
                  createdAt:
                    order.createdAt?.slice(0, 19).replace("T", " ") || "",
                })),
              )
              .catch(() => []),
          ),
        );
        // Nối thành 1 array lớn
        const allDetails = allDetailsArrays.flat();

        // Gộp quantity và list orders theo productId
        const soldMap = {};
        allDetails.forEach((detail) => {
          if (!detail.productId) return;
          if (!soldMap[detail.productId]) {
            soldMap[detail.productId] = {
              soldQuantity: 0,
              ordersIncluded: [],
            };
          }
          soldMap[detail.productId].soldQuantity += Number(
            detail.quantity || 0,
          );
          soldMap[detail.productId].ordersIncluded.push({
            orderId: detail.orderId,
            orderCode: detail.orderCode,
            customer: detail.customer,
            createdAt: detail.createdAt,
            quantity: detail.quantity,
          });
        });

        // Ghép với info sản phẩm từ allProducts
        const soldProducts = Object.entries(soldMap).map(
          ([productId, soldInfo]) => {
            const productInfo =
              allProducts.find((p) => p.id === Number(productId)) || {};
            return {
              ...productInfo,
              soldQuantity: soldInfo.soldQuantity,
              ordersIncluded: soldInfo.ordersIncluded,
            };
          },
        );
        setProducts(soldProducts);
        setLoading(false);
      })
      .catch(() => {
        setProducts([]);
        setLoading(false);
      });
  }, [allProducts.length]);

  // Table header cho modal danh sách sản phẩm đã bán
  const headers = [
    {
      key: "urlImage",
      label: "Hình ảnh",
      render: (item) => (
        <div className="w-12 h-12 overflow-hidden rounded bg-gray-50 flex items-center justify-center">
          {item.urlImage ? (
            <img
              src={item.urlImage}
              alt={item.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-gray-300">No image</span>
          )}
        </div>
      ),
    },
    {
      key: "name",
      label: "Tên sản phẩm",
      render: (item) => (
        <span className="font-medium text-gray-900">{item.name}</span>
      ),
    },
    {
      key: "company",
      label: "Đơn vị",
      render: (item) => (
        <span className="text-gray-700">{item.company?.companyName}</span>
      ),
    },
    {
      key: "price",
      label: "Giá tiền",
      render: (item) => (
        <span>
          {item.isContactPrice === true
            ? "Liên hệ"
            : formatCurrencyVND(item.price)}
        </span>
      ),
    },
    {
      key: "soldQuantity",
      label: "Đã bán",
      render: (item) => (
        <span className="font-bold text-blue-700">{item.soldQuantity}</span>
      ),
    },
  ];

  return (
    <div className="w-full">
      {/* Card ngoài newApple style */}
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="cursor-pointer transition-all p-5 rounded-2xl bg-white border border-green-100 shadow-md"
        onClick={() => setIsOpen(true)}
      >
        <div className="flex flex-col items-center text-center gap-2">
          <div className="p-3 bg-green-100 rounded-full mb-1 flex items-center justify-center">
            <CheckCircleIcon className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <p className="text-black text-lg mb-1">Sản phẩm đã giao dịch</p>
            <p className="text-3xl font-bold ">
              {loading ? "..." : `${products.length}/${allProducts.length}`}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Modal danh sách sản phẩm đã giao dịch */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            {...animation}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
          >
            <motion.div
              {...animation}
              className="relative bg-white w-full max-w-7xl max-h-[90vh] mx-2 rounded-2xl p-6 shadow-xl overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              <div className="mb-4">
                <h2 className="text-xl font-semibold ">
                  Sản phẩm đã giao dịch thành công
                </h2>
              </div>
              {loading ? (
                <div className="text-center py-6 text-gray-400">
                  Đang tải...
                </div>
              ) : (
                <div className="w-full overflow-x-auto">
                  <div className="min-w-[900px] max-w-full mx-auto">
                    <CustomTableComponent
                      headers={headers}
                      data={products}
                      onRowClick={(item) => setSelectedProduct(item)}
                    />
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal chi tiết sản phẩm */}
      <AnimatePresence>
        {selectedProduct && (
          <ProductDetailModal
            product={selectedProduct}
            onClose={() => setSelectedProduct(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductSoldCard;
